import type { Document, DocumentVersion } from "@models";
import {
  Add,
  CheckRounded,
  Comment as CommentIcon,
  ContentCopyRounded,
  Edit,
  HistoryOutlined,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Badge,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Skeleton,
  Tooltip,
} from "@mui/material";
import type { Editor } from "@tiptap/react";
import { useId, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmationModal } from "src/components/ConfirmationModal";
import { DeletedDocumentMoreOptions } from "src/components/DocViewer/components/docViewerTopBar/actions/DeletedDocumentMoreOptions";
import {
  DocViewerMoreOptions,
  type MoreOptionsButton,
} from "src/components/DocViewer/components/docViewerTopBar/actions/DocViewerMoreOptions";
import { useDocumentDataKeysAction } from "src/components/DocViewer/components/docViewerTopBar/actions/useDocumentDataKeysAction";
import DocRevisionDropDown from "src/components/DocViewer/components/docViewerTopBar/selects/DocRevisionDropDown";
import { DocumentDeleteConfirmationContent } from "src/components/Documents/DocumentDeleteConfirmationContent";
import { DocumentScopeChip } from "src/components/Documents/DocumentScopeChip";
import { RenameDocumentDialog } from "src/components/Documents/RenameDocumentDialog";
import { FrameTemplateChip } from "src/components/Frames/FrameTemplateChip";
import GenericButton from "src/components/GenericButton/GenericButton";
import { OutstandingActionPing } from "src/components/GenericButton/OutstandingActionPing";
import { useSnackbar } from "src/components/Snackbar";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import {
  useGetDevice,
  useGetFrameTemplateForDocument,
  useGetUser,
} from "src/hooks/queries";
import { useDeleteDocumentAction } from "src/hooks/useDeleteDocumentAction";
import { fillRouteWithSlugs } from "src/utils/navigation";
import { getOrgFromUser } from "src/utils/user";
import {
  type TiptapTopBarCopyAction,
  useTiptapCopyAction,
} from "./useTiptapCopyAction";
import { useTiptapExportActions } from "./useTiptapExportActions";
import type { TiptapRecordDropdownOptions } from "./useTiptapRecordOptions";

export type { TiptapTopBarCopyAction } from "./useTiptapCopyAction";

type TiptapTopBarPreviewActionBase = {
  ariaLabel: string;
  isLoading?: boolean;
  label: string;
  onClick: () => void;
};

export type TiptapTopBarPreviewAction = TiptapTopBarPreviewActionBase &
  (
    | {
        kind: "close";
        viewingMessage: string;
      }
    | {
        kind: "resume";
      }
  );

export type TiptapTopBarUpdateAnswersAction = {
  isLoading?: boolean;
  label?: string;
  onClick: () => void;
};

export type TiptapTopBarExportAction = {
  isDisabled?: boolean;
  onClick: () => void;
};

type TiptapTopBarDeleteDocumentAction = {
  isDisabled?: boolean;
  onClick: () => void | Promise<void>;
  tooltipTitle?: string;
};

type TiptapTopBarComparison = {
  onExit: () => void;
  sourceTitle: string;
  targetTitle: string | null;
};

type TiptapDocViewerTopBarProps = {
  canCreateNewRevision?: boolean;
  canResumeEditing?: boolean;
  collaborationActivityCount?: number;
  comparison?: TiptapTopBarComparison | null;
  copyAction?: TiptapTopBarCopyAction | null;
  copyEditor?: Editor | null;
  docxExportAction?: TiptapTopBarExportAction | null;
  document?: Document | null;
  isCollaborationPanelOpen: boolean;
  isActionsLoading?: boolean;
  isApprovalDisabled?: boolean;
  isCopyDisabled?: boolean;
  isExportDisabled?: boolean;
  hasOutstandingApproval?: boolean;
  isVersionsPanelOpen?: boolean;
  isCreatingNewRevision?: boolean;
  isDeleted?: boolean;
  isResumingEditing?: boolean;
  hideUnavailableEditButton?: boolean;
  markdownExportAction?: TiptapTopBarExportAction | null;
  onApprovalClick: () => void;
  onCreateNewRevision?: () => void;
  documentScopeLabel?: string | null;
  pdfExportAction?: TiptapTopBarExportAction | null;
  previewAction?: TiptapTopBarPreviewAction | null;
  recordOptions?: TiptapRecordDropdownOptions | null;
  selectedDocVersion?: DocumentVersion | null;
  showEditButton?: boolean;
  onResumeEditing?: () => void;
  onToggleCollaborationPanel: () => void;
  onToggleVersionsPanel?: () => void;
  updateAnswersAction?: TiptapTopBarUpdateAnswersAction | null;
};

type TiptapExportActionsConfig = Parameters<typeof useTiptapExportActions>[0];

const TiptapEditMenuButton = ({
  isDisabled = false,
  primaryAction,
  updateAnswersAction,
}: {
  isDisabled?: boolean;
  primaryAction?: TiptapTopBarPreviewAction | null;
  updateAnswersAction?: TiptapTopBarUpdateAnswersAction | null;
}) => {
  const normalButtons: MoreOptionsButton[] = [
    ...(primaryAction
      ? [
          {
            title: primaryAction.label,
            onClick: primaryAction.onClick,
            disabled: isDisabled || primaryAction.isLoading,
          },
        ]
      : []),
    ...(updateAnswersAction
      ? [
          {
            title: updateAnswersAction.label ?? "Update Answers",
            onClick: updateAnswersAction.onClick,
            disabled: isDisabled || updateAnswersAction.isLoading,
          },
        ]
      : []),
  ];

  if (!isDisabled && normalButtons.length === 0) {
    return null;
  }

  const isMenuDisabled =
    isDisabled || normalButtons.every((button) => button.disabled);
  const isTriggerLoading =
    Boolean(primaryAction?.isLoading || updateAnswersAction?.isLoading) &&
    isMenuDisabled;

  return (
    <DocViewerMoreOptions
      dataTestId="tiptap-keep-editing-menu-button"
      hideTriggerTooltip={false}
      isDisabled={isMenuDisabled}
      menuId="tiptap-keep-editing-menu"
      normalButtons={normalButtons}
      triggerAriaLabel={primaryAction?.ariaLabel ?? "Edit actions"}
      triggerIcon={
        isTriggerLoading ? (
          <CircularProgress size="1rem" color="inherit" />
        ) : (
          <Edit fontSize="small" data-testid="keep-editing-button" />
        )
      }
      triggerTooltipTitle={primaryAction?.label ?? "Edit"}
    />
  );
};

const TiptapCopyButton = ({
  copyAction,
}: {
  copyAction?: TiptapTopBarCopyAction | null;
}) => {
  const isCopied = Boolean(copyAction?.isCopied);
  const isDisabled = !copyAction || copyAction.isDisabled;

  return (
    <GenericButton
      ariaLabel={isCopied ? "Editor content copied" : "Copy editor content"}
      dataTestId="tiptap-copy-content-button"
      isDisabled={isDisabled}
      onClick={() => copyAction?.onClick()}
      tooltipContent={isCopied ? "Copied" : "Copy editor content"}
    >
      {isCopied ? (
        <CheckRounded color="success" fontSize="small" />
      ) : (
        <ContentCopyRounded fontSize="small" />
      )}
    </GenericButton>
  );
};

const TiptapTopBarActionsLoading = () => (
  <div
    aria-label="Loading editor actions"
    aria-busy="true"
    className="flex min-w-[260px] items-center justify-end pr-1"
    role="status"
  >
    <Skeleton aria-hidden="true" height={32} variant="rounded" width={220} />
  </div>
);

const TiptapMoreOptionsMenuButton = ({
  dataKeysButton,
  deleteDocumentAction,
  docxExportAction,
  markdownExportAction,
  onRenameDocument,
  pdfExportAction,
}: {
  dataKeysButton?: MoreOptionsButton | null;
  deleteDocumentAction?: TiptapTopBarDeleteDocumentAction | null;
  docxExportAction?: TiptapTopBarExportAction | null;
  markdownExportAction?: TiptapTopBarExportAction | null;
  onRenameDocument: () => void;
  pdfExportAction?: TiptapTopBarExportAction | null;
}) => {
  const [deleteDocumentModalOpen, setDeleteDocumentModalOpen] = useState(false);
  const downloadActions = [
    { action: docxExportAction, title: "DOCX" },
    { action: pdfExportAction, title: "PDF" },
    { action: markdownExportAction, title: "Markdown" },
  ];
  const isActionDisabled = (
    action: TiptapTopBarExportAction | null | undefined
  ) => !action || action.isDisabled;
  const isDownloadDisabled = downloadActions.every(({ action }) =>
    isActionDisabled(action)
  );
  const normalButtons: MoreOptionsButton[] = [
    {
      title: "Rename document",
      disabled: false,
      onClick: onRenameDocument,
    },
    {
      title: "Download as",
      disabled: isDownloadDisabled,
      subButtons: downloadActions.map(({ action, title }) => ({
        title,
        disabled: isActionDisabled(action),
        onClick: () => {
          if (!action || action.isDisabled) {
            return;
          }

          action.onClick();
        },
      })),
    },
    ...(dataKeysButton ? [dataKeysButton] : []),
  ];
  const dangerZoneButtons: MoreOptionsButton[] = deleteDocumentAction
    ? [
        {
          title: "Delete document",
          onClick: () => setDeleteDocumentModalOpen(true),
          disabled: deleteDocumentAction.isDisabled,
          tooltipTitle: deleteDocumentAction.tooltipTitle,
        },
      ]
    : [];

  const handleDeleteDocument = async () => {
    if (!deleteDocumentAction || deleteDocumentAction.isDisabled) {
      return;
    }

    setDeleteDocumentModalOpen(false);
    await deleteDocumentAction.onClick();
  };

  return (
    <>
      <DocViewerMoreOptions
        dangerZoneButtons={dangerZoneButtons}
        dataTestId="tiptap-more-options-button"
        hideTriggerTooltip={false}
        menuId="tiptap-more-options-menu"
        normalButtons={normalButtons}
        triggerAriaLabel="More options"
        triggerTooltipTitle="More options"
      />

      <ConfirmationModal
        title="Delete document"
        confirmText="Confirm"
        content={<DocumentDeleteConfirmationContent />}
        onAbort={() => setDeleteDocumentModalOpen(false)}
        onConfirm={handleDeleteDocument}
        open={deleteDocumentModalOpen}
      />
    </>
  );
};

const TiptapRegisteredMoreOptionsMenuButton = (
  props: TiptapExportActionsConfig & {
    dataKeysButton?: MoreOptionsButton | null;
    deleteDocumentAction: TiptapTopBarDeleteDocumentAction;
    onRenameDocument: () => void;
  }
) => {
  const {
    dataKeysButton,
    deleteDocumentAction,
    onRenameDocument,
    ...exportConfig
  } = props;
  const exportActions = useTiptapExportActions(exportConfig);

  return (
    <TiptapMoreOptionsMenuButton
      dataKeysButton={dataKeysButton}
      deleteDocumentAction={deleteDocumentAction}
      docxExportAction={exportActions.docx}
      markdownExportAction={exportActions.markdown}
      onRenameDocument={onRenameDocument}
      pdfExportAction={exportActions.pdf}
    />
  );
};

const TiptapAutoMoreOptionsMenuButton = ({
  dataKeysButton,
  deleteDocumentAction,
  disabled = false,
  document,
  editor,
  onRenameDocument,
  selectedDocVersion,
}: {
  dataKeysButton?: MoreOptionsButton | null;
  deleteDocumentAction: TiptapTopBarDeleteDocumentAction;
  disabled?: boolean;
  document: Document;
  editor?: Editor | null;
  onRenameDocument: () => void;
  selectedDocVersion?: DocumentVersion | null;
}) => {
  const { orgId = "" } = useParams<{ orgId: string }>();
  const { data: user } = useGetUser();
  const { data: device } = useGetDevice({
    orgId,
    deviceId: document.deviceId ?? "",
  });
  const organizationName = getOrgFromUser(user, orgId)?.name?.trim() ?? "";

  if (!editor || !selectedDocVersion?.signatureTable || !organizationName) {
    return (
      <TiptapMoreOptionsMenuButton
        dataKeysButton={dataKeysButton}
        deleteDocumentAction={deleteDocumentAction}
        onRenameDocument={onRenameDocument}
      />
    );
  }

  return (
    <TiptapRegisteredMoreOptionsMenuButton
      dataKeysButton={dataKeysButton}
      deleteDocumentAction={deleteDocumentAction}
      deviceName={document.deviceId ? device?.name : null}
      disabled={disabled}
      document={document}
      editor={editor}
      organizationName={organizationName}
      orgId={orgId}
      onRenameDocument={onRenameDocument}
      selectedDocVersion={selectedDocVersion}
    />
  );
};

type TiptapResolvedMoreOptionsMenuButtonProps = {
  dataKeysButton?: MoreOptionsButton | null;
  deleteDocumentAction: TiptapTopBarDeleteDocumentAction;
  disabled?: boolean;
  document: Document;
  docxExportAction?: TiptapTopBarExportAction | null;
  editor?: Editor | null;
  markdownExportAction?: TiptapTopBarExportAction | null;
  onRenameDocument: () => void;
  pdfExportAction?: TiptapTopBarExportAction | null;
  selectedDocVersion?: DocumentVersion | null;
};

const TiptapResolvedMoreOptionsMenuButton = ({
  dataKeysButton,
  deleteDocumentAction,
  disabled = false,
  document,
  docxExportAction,
  editor,
  markdownExportAction,
  onRenameDocument,
  pdfExportAction,
  selectedDocVersion,
}: TiptapResolvedMoreOptionsMenuButtonProps) => {
  const hasExportActionOverrides =
    docxExportAction !== undefined ||
    markdownExportAction !== undefined ||
    pdfExportAction !== undefined;

  if (hasExportActionOverrides) {
    return (
      <TiptapMoreOptionsMenuButton
        dataKeysButton={dataKeysButton}
        deleteDocumentAction={deleteDocumentAction}
        docxExportAction={docxExportAction}
        markdownExportAction={markdownExportAction}
        onRenameDocument={onRenameDocument}
        pdfExportAction={pdfExportAction}
      />
    );
  }

  return (
    <TiptapAutoMoreOptionsMenuButton
      dataKeysButton={dataKeysButton}
      deleteDocumentAction={deleteDocumentAction}
      disabled={disabled}
      document={document}
      editor={editor}
      onRenameDocument={onRenameDocument}
      selectedDocVersion={selectedDocVersion}
    />
  );
};

const TiptapDataKeysMoreOptionsMenuButton = ({
  disableDataKeyEditing,
  selectedDocVersion,
  ...moreOptionsProps
}: Omit<
  TiptapResolvedMoreOptionsMenuButtonProps,
  "dataKeysButton" | "selectedDocVersion"
> & {
  disableDataKeyEditing: boolean;
  selectedDocVersion: DocumentVersion;
}) => {
  const { deviceId = "", orgId = "" } = useParams<{
    deviceId?: string;
    orgId?: string;
  }>();
  const { data: user } = useGetUser();
  const canEditDataKeys =
    !disableDataKeyEditing &&
    !!user?.permissions?.includes("write:configuration") &&
    selectedDocVersion.revision === null &&
    selectedDocVersion.readyForApproval === false;
  const { dataKeysButton, dataKeysModal } = useDocumentDataKeysAction({
    canEditDataKeys,
    deviceId,
    orgId,
    selectedDoc: moreOptionsProps.document,
    selectedVersion: selectedDocVersion,
  });

  return (
    <>
      <TiptapResolvedMoreOptionsMenuButton
        {...moreOptionsProps}
        dataKeysButton={dataKeysButton}
        selectedDocVersion={selectedDocVersion}
      />
      {dataKeysModal}
    </>
  );
};

const TiptapDocumentMoreOptionsMenuButton = ({
  disableDataKeyEditing,
  ...moreOptionsProps
}: TiptapResolvedMoreOptionsMenuButtonProps & {
  disableDataKeyEditing: boolean;
}) => {
  if (!moreOptionsProps.selectedDocVersion) {
    return <TiptapResolvedMoreOptionsMenuButton {...moreOptionsProps} />;
  }

  return (
    <TiptapDataKeysMoreOptionsMenuButton
      {...moreOptionsProps}
      disableDataKeyEditing={disableDataKeyEditing}
      selectedDocVersion={moreOptionsProps.selectedDocVersion}
    />
  );
};

export const TiptapDocViewerTopBar = ({
  canCreateNewRevision = false,
  canResumeEditing = false,
  collaborationActivityCount = 0,
  comparison = null,
  copyAction: copyActionOverride,
  copyEditor = null,
  docxExportAction,
  document,
  isCollaborationPanelOpen,
  isActionsLoading = false,
  isApprovalDisabled = false,
  isCopyDisabled = false,
  isExportDisabled = false,
  hasOutstandingApproval = false,
  isVersionsPanelOpen = false,
  isCreatingNewRevision = false,
  isDeleted: isDeletedProp = false,
  isResumingEditing = false,
  hideUnavailableEditButton = false,
  markdownExportAction,
  onApprovalClick,
  onCreateNewRevision,
  documentScopeLabel = null,
  pdfExportAction,
  previewAction = null,
  recordOptions = null,
  selectedDocVersion = null,
  showEditButton = true,
  onResumeEditing,
  onToggleCollaborationPanel,
  onToggleVersionsPanel,
  updateAnswersAction = null,
}: TiptapDocViewerTopBarProps) => {
  const isDeleted = isDeletedProp || Boolean(document?.softDeletionEvent);
  const comparisonAlertMessageId = useId();
  const [renameDocumentDialogOpen, setRenameDocumentDialogOpen] =
    useState(false);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { deviceId = "", orgId = "" } = useParams<{
    deviceId?: string;
    orgId?: string;
  }>();
  const {
    canDeleteDocument,
    deleteDocument,
    getDeleteDocumentTooltipTitle,
    isDeleteDocumentPending,
  } = useDeleteDocumentAction({
    deviceId: document?.deviceId ?? deviceId,
    documents: document ? [document] : [],
    orgId,
  });
  const registeredCopyAction = useTiptapCopyAction({
    disabled: isCopyDisabled || isDeleted,
    editor: copyEditor,
  });
  const copyAction =
    copyActionOverride === undefined
      ? registeredCopyAction
      : copyActionOverride;
  const { data: frameTemplate } = useGetFrameTemplateForDocument(
    orgId,
    document
  );

  if (!document) {
    return null;
  }

  if (isDeleted) {
    return (
      <div>
        <div className="flex min-h-[49px] items-center justify-between border-0 border-b border-solid border-gray-200 px-3 py-2">
          <div className="flex min-w-0 items-center gap-2">
            <Chip color="error" label="Deleted" size="xxs" />
            {documentScopeLabel && (
              <DocumentScopeChip
                dataTestId="document-viewer-scope-chip"
                label={documentScopeLabel}
                maxWidth={220}
              />
            )}
            {frameTemplate && (
              <FrameTemplateChip
                dataTestId="document-viewer-template-chip"
                maxWidth={220}
                orgId={orgId}
                templateId={frameTemplate.id}
                templateTitle={frameTemplate.defaultTitle}
              />
            )}
          </div>
          <DeletedDocumentMoreOptions
            dataTestId="tiptap-more-options-button"
            documentId={document.id}
          />
        </div>
        <Alert severity="info">
          This document has been deleted and is read-only.
        </Alert>
      </div>
    );
  }

  const deleteDocumentAction: TiptapTopBarDeleteDocumentAction = {
    isDisabled: isDeleteDocumentPending || !canDeleteDocument(document),
    onClick: async () => {
      try {
        await deleteDocument({
          document,
          navigateOnDelete: false,
          suppressErrorSnackbar: true,
        });
      } catch {
        showSnackbar("Could not delete document. Please try again.");
        return;
      }

      navigate(
        fillRouteWithSlugs(ROUTES.DOCUMENTS, {
          [ROUTE_SLUGS.ORG_ID]: orgId,
        }),
        { replace: true }
      );
    },
    tooltipTitle: getDeleteDocumentTooltipTitle(document),
  };

  const isSavedVersionPreview = previewAction?.kind === "close";
  const isVersionHistoryMode = isSavedVersionPreview || Boolean(comparison);
  const visibleUpdateAnswersAction = isVersionHistoryMode
    ? null
    : updateAnswersAction;
  const showResumeEditing =
    !isVersionHistoryMode &&
    !previewAction &&
    canResumeEditing &&
    !!onResumeEditing;
  const showCreateRevision =
    !isVersionHistoryMode &&
    !previewAction &&
    canCreateNewRevision &&
    !!onCreateNewRevision;
  const primaryEditAction: TiptapTopBarPreviewAction | null = showResumeEditing
    ? {
        ariaLabel: "Edit Content",
        isLoading: isResumingEditing,
        kind: "resume",
        label: "Edit Content",
        onClick: () => onResumeEditing?.(),
      }
    : previewAction?.kind === "resume"
      ? previewAction
      : null;
  const hasEditActions = Boolean(
    primaryEditAction || visibleUpdateAnswersAction
  );
  const shouldDisableEditMenu = !showEditButton || !hasEditActions;
  const shouldHideEditMenu = hideUnavailableEditButton && !hasEditActions;
  const showDiffAlert = !isActionsLoading && Boolean(comparison);
  const versionPreviewAction =
    previewAction?.kind === "close" ? previewAction : null;
  const showVersionPreviewAlert =
    !isActionsLoading && !showDiffAlert && Boolean(versionPreviewAction);
  const showModeAlert = showDiffAlert || showVersionPreviewAlert;
  const versionPreviewMessage =
    versionPreviewAction?.viewingMessage ?? "Viewing: Saved Version";
  const diffAlertContent = comparison ? (
    <span id={comparisonAlertMessageId}>
      Comparing version <strong>{comparison.sourceTitle}</strong> with{" "}
      {comparison.targetTitle ? (
        <strong>{comparison.targetTitle}</strong>
      ) : (
        "current"
      )}
    </span>
  ) : null;

  return (
    <div>
      <div className="flex min-h-[49px] items-center justify-between border-0 border-b border-solid border-gray-200 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {recordOptions && (
            <DocRevisionDropDown {...recordOptions} standalone />
          )}
          {documentScopeLabel && (
            <DocumentScopeChip
              dataTestId="document-viewer-scope-chip"
              label={documentScopeLabel}
              maxWidth={220}
            />
          )}
          {frameTemplate && (
            <FrameTemplateChip
              dataTestId="document-viewer-template-chip"
              maxWidth={220}
              orgId={orgId}
              templateId={frameTemplate.id}
              templateTitle={frameTemplate.defaultTitle}
            />
          )}
        </div>

        <div className="flex min-h-[32px] items-center justify-end gap-2">
          {isActionsLoading ? (
            <TiptapTopBarActionsLoading />
          ) : (
            <>
              {!isVersionHistoryMode && !shouldHideEditMenu ? (
                <TiptapEditMenuButton
                  isDisabled={shouldDisableEditMenu}
                  primaryAction={primaryEditAction}
                  updateAnswersAction={visibleUpdateAnswersAction}
                />
              ) : null}

              {showCreateRevision && (
                <LoadingButton
                  size="small"
                  variant="outlined"
                  loading={isCreatingNewRevision}
                  startIcon={<Add fontSize="small" />}
                  onClick={() => onCreateNewRevision?.()}
                >
                  New revision
                </LoadingButton>
              )}

              <TiptapCopyButton copyAction={copyAction} />

              <TiptapDocumentMoreOptionsMenuButton
                deleteDocumentAction={deleteDocumentAction}
                disableDataKeyEditing={isVersionHistoryMode}
                disabled={isExportDisabled}
                document={document}
                docxExportAction={docxExportAction}
                editor={copyEditor}
                markdownExportAction={markdownExportAction}
                onRenameDocument={() => setRenameDocumentDialogOpen(true)}
                pdfExportAction={pdfExportAction}
                selectedDocVersion={selectedDocVersion}
              />

              <span className="relative inline-flex">
                <Tooltip title="Open approval panel">
                  <span>
                    <Button
                      color="inherit"
                      disabled={isApprovalDisabled}
                      onClick={() => onApprovalClick()}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: "divider",
                        borderRadius: 1,
                        minHeight: 30,
                        minWidth: 0,
                        px: 1,
                        textTransform: "none",
                      }}
                    >
                      Approval
                    </Button>
                  </span>
                </Tooltip>
                {hasOutstandingApproval && (
                  <OutstandingActionPing dataTestId="approval-outstanding-action-ping" />
                )}
              </span>

              <div className="flex items-center">
                <Tooltip
                  title={
                    isVersionsPanelOpen ? "Close versions" : "Open versions"
                  }
                >
                  <IconButton
                    aria-label={
                      isVersionsPanelOpen
                        ? "Close versions panel"
                        : "Open versions panel"
                    }
                    onClick={onToggleVersionsPanel}
                    size="small"
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      marginRight: 1,
                    }}
                  >
                    <HistoryOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={
                    isCollaborationPanelOpen
                      ? "Close collaboration"
                      : "Open collaboration"
                  }
                >
                  <Badge
                    badgeContent={
                      collaborationActivityCount > 0
                        ? collaborationActivityCount
                        : undefined
                    }
                    color="primary"
                    max={99}
                    variant={
                      collaborationActivityCount > 0 ? "standard" : "dot"
                    }
                    invisible={
                      collaborationActivityCount === 0 &&
                      !isCollaborationPanelOpen
                    }
                  >
                    <IconButton
                      aria-label={
                        isCollaborationPanelOpen
                          ? "Close collaboration panel"
                          : "Open collaboration panel"
                      }
                      onClick={onToggleCollaborationPanel}
                      size="small"
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <CommentIcon fontSize="small" />
                    </IconButton>
                  </Badge>
                </Tooltip>
              </div>
            </>
          )}
        </div>
      </div>
      <Collapse in={showModeAlert} mountOnEnter unmountOnExit>
        <Alert
          aria-label={
            showVersionPreviewAlert ? versionPreviewMessage : undefined
          }
          aria-labelledby={comparison ? comparisonAlertMessageId : undefined}
          action={
            comparison ? (
              <LoadingButton
                onClick={comparison.onExit}
                size="small"
                sx={{ whiteSpace: "nowrap" }}
                variant="text"
              >
                Exit comparison
              </LoadingButton>
            ) : showVersionPreviewAlert && versionPreviewAction ? (
              <LoadingButton
                aria-label={versionPreviewAction.ariaLabel}
                loading={versionPreviewAction.isLoading}
                onClick={versionPreviewAction.onClick}
                size="small"
                sx={{ whiteSpace: "nowrap" }}
                variant="text"
              >
                {versionPreviewAction.label}
              </LoadingButton>
            ) : null
          }
          severity="info"
          sx={{
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            borderRadius: 0,
            px: "10.5px",
            py: 0.5,
            "& .MuiAlert-action": {
              alignItems: "center",
              mr: 0,
              pl: 2,
              py: 0,
            },
            "& .MuiAlert-icon": {
              alignItems: "center",
              py: 0,
            },
            "& .MuiAlert-message": {
              alignItems: "center",
              display: "flex",
              py: 0,
            },
          }}
        >
          {showVersionPreviewAlert ? versionPreviewMessage : diffAlertContent}
        </Alert>
      </Collapse>
      {renameDocumentDialogOpen && (
        <RenameDocumentDialog
          deviceId={document.deviceId ?? deviceId}
          document={document}
          onClose={() => setRenameDocumentDialogOpen(false)}
          orgId={orgId}
        />
      )}
    </div>
  );
};
